import {
  buildConductorToCableIndex,
  type ConductorId,
  type Device,
  type DomainMaps,
  listConductors,
  listDeviceLayoutsForSheet,
  listDevices,
  listSheetsInOrder,
  listTerminals,
  type SheetId,
} from "@electro-planner/domain";
import { getSymbol } from "@electro-planner/symbols";

// Svenska Excel-versioner förväntar sig semikolon som separator (eftersom
// decimal-komma är default i sv-SE) och CRLF-radbrytning. Det här är också
// vad LibreOffice Calc sv-SE väljer i import-dialogen.
const DELIMITER = ";";
const LINE_BREAK = "\r\n";
// UTF-8 BOM säkerställer att Excel öppnar filen med korrekta å/ä/ö.
export const UTF8_BOM = "\ufeff";

export type CsvTable = {
  headers: string[];
  rows: string[][];
};

function escapeCell(v: string | number | undefined | null): string {
  if (v == null) return "";
  const s = typeof v === "number" ? formatNumber(v) : String(v);
  // RFC 4180-stil: citera bara celler som innehåller separator, citattecken
  // eller radbrytning. Dubbel-escapea inre citattecken.
  if (s.includes(DELIMITER) || s.includes('"') || s.includes("\n") || s.includes("\r")) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
}

function formatNumber(n: number): string {
  if (!Number.isFinite(n)) return "";
  // Svensk decimal-komma. Excel sv-SE tolkar "1,5" som tal men "1.5" som text.
  if (Number.isInteger(n)) return String(n);
  return String(n).replace(".", ",");
}

export function serializeCsv(table: CsvTable): string {
  const lines: string[] = [];
  lines.push(table.headers.map(escapeCell).join(DELIMITER));
  for (const row of table.rows) lines.push(row.map(escapeCell).join(DELIMITER));
  return UTF8_BOM + lines.join(LINE_BREAK) + LINE_BREAK;
}

/**
 * Apparatlista: en rad per device, sorterat på (ark-index, beteckning).
 * symbol-id är stabilt identifierare för inköp; typ är mänskligt språk på svenska.
 * Om en device inte har layout på något ark visas den med tomt ark-fält så den
 * ändå kommer med i inköpslistan.
 */
export function buildBomTable(maps: DomainMaps): CsvTable {
  const devices = listDevices(maps);
  const sheets = listSheetsInOrder(maps);

  const layoutsBySheet = new Map<
    SheetId,
    ReturnType<typeof listDeviceLayoutsForSheet>
  >();
  for (const s of sheets) layoutsBySheet.set(s.id, listDeviceLayoutsForSheet(maps, s.id));

  const rows: Array<[string, string, string, string, string, string, string]> = [];
  const deviceById = new Map<string, Device>(devices.map((d) => [d.id, d]));
  const placed = new Set<string>();

  sheets.forEach((sheet, idx) => {
    const layouts = layoutsBySheet.get(sheet.id) ?? [];
    for (const layout of layouts) {
      const device = deviceById.get(layout.deviceId);
      if (!device) continue;
      placed.add(device.id);
      const symbolId = getSymbolId(device);
      const symbol = symbolId ? getSymbol(symbolId) : null;
      rows.push([
        device.designation,
        symbol?.label.sv ?? "",
        symbolId ?? "",
        `${idx + 1}`,
        String(Math.round(layout.x)),
        String(Math.round(layout.y)),
        `${layout.rotation}°`,
      ]);
    }
  });

  // Devices utan layout (föräldralösa — ska inte hända i normal användning
  // men visa dem hellre än att tysta bort dem från inköpslistan).
  for (const device of devices) {
    if (placed.has(device.id)) continue;
    const symbolId = getSymbolId(device);
    const symbol = symbolId ? getSymbol(symbolId) : null;
    rows.push([
      device.designation,
      symbol?.label.sv ?? "",
      symbolId ?? "",
      "",
      "",
      "",
      "",
    ]);
  }

  rows.sort((a, b) => {
    const sa = a[3] || "~";
    const sb = b[3] || "~";
    if (sa !== sb) return sa.localeCompare(sb, "sv");
    return a[0].localeCompare(b[0], "sv", { numeric: true });
  });

  return {
    headers: ["Beteckning", "Typ", "Symbol-id", "Ark", "X", "Y", "Rotation"],
    rows,
  };
}

/**
 * Trådlista: en rad per conductor med anslutningspunkter som "K1:1" (device-
 * beteckning + terminal-label). Ark sätts till arket där BÅDA ändarna är
 * placerade; saknas layout lämnas cellen tom.
 */
export function buildConductorTable(maps: DomainMaps): CsvTable {
  const conductors = listConductors(maps);
  const terminals = listTerminals(maps);
  const devices = listDevices(maps);
  const sheets = listSheetsInOrder(maps);
  const cableByConductor = buildConductorToCableIndex(maps);

  const deviceById = new Map<string, Device>(devices.map((d) => [d.id, d]));
  const terminalById = new Map(terminals.map((t) => [t.id, t] as const));

  // För varje device: vilken ark-INDEX (1-baserad) den sitter på.
  const sheetIndexByDevice = new Map<string, number>();
  sheets.forEach((sheet, idx) => {
    const layouts = listDeviceLayoutsForSheet(maps, sheet.id);
    for (const l of layouts) {
      if (!sheetIndexByDevice.has(l.deviceId)) {
        sheetIndexByDevice.set(l.deviceId, idx + 1);
      }
    }
  });

  const rows: string[][] = [];
  let n = 0;
  for (const c of conductors) {
    n += 1;
    const fromT = terminalById.get(c.fromTerminal);
    const toT = terminalById.get(c.toTerminal);
    const fromDev = fromT ? deviceById.get(fromT.deviceId) : undefined;
    const toDev = toT ? deviceById.get(toT.deviceId) : undefined;
    const fromSheet = fromT ? sheetIndexByDevice.get(fromT.deviceId) : undefined;
    const toSheet = toT ? sheetIndexByDevice.get(toT.deviceId) : undefined;
    const sheet = fromSheet === toSheet ? (fromSheet ?? "") : `${fromSheet ?? "?"}→${toSheet ?? "?"}`;
    const cable = cableByConductor.get(c.id as ConductorId);
    rows.push([
      `W${n}`,
      cable?.designation ?? "",
      cable?.type ?? "",
      c.attributes.circuitLabel ?? "",
      c.attributes.area != null ? formatNumber(c.attributes.area) : "",
      c.attributes.color ? c.attributes.color.toUpperCase() : "",
      fromDev && fromT ? `${fromDev.designation}:${fromT.label}` : "",
      toDev && toT ? `${toDev.designation}:${toT.label}` : "",
      String(sheet),
    ]);
  }

  return {
    headers: [
      "Nr",
      "Kabel",
      "Kabeltyp",
      "Krets",
      "Area (mm²)",
      "Färg",
      "Från",
      "Till",
      "Ark",
    ],
    rows,
  };
}

function getSymbolId(device: Device): string | undefined {
  const v = device.attributes["symbolId"];
  return typeof v === "string" ? v : undefined;
}

export function buildBomCsv(maps: DomainMaps): string {
  return serializeCsv(buildBomTable(maps));
}

export function buildConductorCsv(maps: DomainMaps): string {
  return serializeCsv(buildConductorTable(maps));
}

/**
 * Triggar browser-download av en UTF-8-kodad csv-string. UTF-8 BOM är redan
 * prependad av serializeCsv så kommunerna öppnar filen korrekt i Excel sv-SE.
 */
export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Release efter nästa tick så att browsern hinner initiera nedladdningen.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
